import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { folderApi } from '../../../../shared/services/folderApi'
import type { FolderNode } from './types'
import { useTranslation } from 'react-i18next'

interface EditFolderModalProps {
  folder: FolderNode
  onClose: () => void
  onFolderUpdated: () => void
}

/** {t('form.edit')} 모달 */
const EditFolderModal: React.FC<EditFolderModalProps> = ({
  folder,
  onClose,
  onFolderUpdated,
}) => {
  const { t } = useTranslation()
  const [name, setName] = useState(folder.name)
  const [color, setColor] = useState(folder.color)
  const [description, setDescription] = useState(folder.description || '')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      return
    }

    setLoading(true)
    try {
      await folderApi.update(folder._id, {
        name: name.trim(),
        color,
        description: description.trim() || undefined,
      })
      onFolderUpdated()
    } catch (error) {
      console.error('폴더 수정 실패:', error)
      toast.error(
        error instanceof Error ? error.message : t('form.editFolderError')
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-96 rounded-lg bg-white p-6">
        <h3 className="mb-4 text-lg font-semibold">{t('form.edit')}</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {t('form.folderName')}
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder={t('form.folderNamePlaceholder')}
              autoFocus
            />
          </div>
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {t('form.description')}
            </label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
              placeholder="폴더 설명을 입력하세요"
            />
          </div>
          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {t('form.color')}
            </label>
            <div className="space-y-3">
              {/* 기본 색상 팔레트 */}
              <div className="grid grid-cols-8 gap-2">
                {[
                  '#3B82F6',
                  '#10B981',
                  '#F59E0B',
                  '#EF4444',
                  '#8B5CF6',
                  '#06B6D4',
                  '#F97316',
                  '#84CC16',
                  '#EC4899',
                  '#6366F1',
                  '#14B8A6',
                  '#F43F5E',
                  '#8E4EC6',
                  '#059669',
                  '#DC2626',
                  '#7C3AED',
                  '#0891B2',
                  '#EA580C',
                  '#65A30D',
                  '#BE185D',
                  '#4F46E5',
                  '#0D9488',
                  '#B91C1C',
                  '#7C2D12',
                ].map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`h-8 w-8 rounded-full border-2 transition-all hover:scale-110 ${
                      color === c
                        ? 'border-gray-900 shadow-lg'
                        : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>

              {/* 파스텔 톤 */}
              <div className="grid grid-cols-8 gap-2">
                {[
                  '#DBEAFE',
                  '#D1FAE5',
                  '#FEF3C7',
                  '#FEE2E2',
                  '#EDE9FE',
                  '#CFFAFE',
                  '#FFEDD5',
                  '#ECFDF5',
                  '#FCE7F3',
                  '#E0E7FF',
                  '#CCFBF1',
                  '#FFE4E6',
                  '#F3E8FF',
                  '#A7F3D0',
                  '#FECACA',
                  '#C7D2FE',
                  '#99F6E4',
                  '#FED7AA',
                  '#D9F99D',
                  '#F9A8D4',
                  '#A5B4FC',
                  '#5EEAD4',
                  '#FCA5A5',
                  '#DDD6FE',
                ].map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`h-8 w-8 rounded-full border-2 transition-all hover:scale-110 ${
                      color === c
                        ? 'border-gray-900 shadow-lg'
                        : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>

              {/* 커스텀 색상 선택 */}
              <div className="flex items-center space-x-3 border-t border-gray-200 pt-2">
                <label className="text-sm text-gray-600">커스텀:</label>
                <input
                  type="color"
                  value={color}
                  onChange={e => setColor(e.target.value)}
                  className="h-8 w-10 cursor-pointer rounded border border-gray-300"
                  title={t('form.customColor')}
                />
                <span className="font-mono text-xs text-gray-500">
                  {color.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? t('form.editing') : t('form.edit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditFolderModal
